import { useEffect, useState, useRef } from 'react';
import { createBlock } from '../../../DocEditor';
import { SopAPI } from '../../../../utils/api';
import { useDocVariablesLiveSync } from '../../../../hooks/useDocVariablesLiveSync';
import { collectTicks } from '../utils/tickTree';

/**
 * Fetches the library doc and seeds display state. Master tick defaults come
 * from the RAW body; mission overrides are layered on for display only.
 */
export function useLibraryDocLoad(docId, tickOverridesRef) {
  const [doc, setDoc] = useState(null);
  const [blocks, setBlocks] = useState([]);
  const [localTickOverrides, setLocalTickOverrides] = useState({});
  const [loadError, setLoadError] = useState('');
  const [docVariables, setDocVariables] = useState([]);
  const [variablesColumnWidths, setVariablesColumnWidths] = useState(null);
  const [title, setTitle] = useState('');

  const masterTicksRef = useRef(new Map());
  const lastSavedBodyRef = useRef(null);
  const lastReportedTicksRef = useRef(JSON.stringify(tickOverridesRef.current || {}));

  useDocVariablesLiveSync(docId, setDocVariables);

  useEffect(() => {
    let cancelled = false;
    setLoadError('');
    // Surface-first: swap to skeleton body immediately on doc change so the
    // previous tab's content doesn't linger. MissionDocSheet keeps the
    // Find/Code/Uncheck-all toolbar shell mounted so chrome doesn't jerk.
    setDoc(null);
    setBlocks([]);
    setTitle('');
    SopAPI.get(docId)
      .then((full) => {
        if (cancelled) return;
        const body =
          Array.isArray(full.body) && full.body.length > 0
            ? full.body
            : [createBlock('text')];
        masterTicksRef.current = collectTicks(body);
        lastSavedBodyRef.current = JSON.stringify(body);
        const overrides = tickOverridesRef.current || {};
        lastReportedTicksRef.current = JSON.stringify(overrides);
        setLocalTickOverrides(overrides);
        setDoc(full);
        setTitle(full.title || '');
        setDocVariables(Array.isArray(full.variables) ? full.variables : []);
        setVariablesColumnWidths(
          Array.isArray(full.variablesColumnWidths) ? full.variablesColumnWidths : null
        );
        setBlocks(body);
      })
      .catch((e) => {
        if (!cancelled) setLoadError(e.message || 'Failed to load this doc');
      });
    return () => {
      cancelled = true;
    };
  }, [docId, tickOverridesRef]);

  return {
    doc,
    setDoc,
    blocks,
    setBlocks,
    title,
    setTitle,
    loadError,
    docVariables,
    setDocVariables,
    variablesColumnWidths,
    setVariablesColumnWidths,
    localTickOverrides,
    setLocalTickOverrides,
    masterTicksRef,
    lastSavedBodyRef,
    lastReportedTicksRef,
  };
}
